import React from "react";
import { Box, Skeleton } from "@mui/material";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../../store/platform-slice";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import { BioAgeClockFigure } from "../../../../consultation/components/shared/BioAgeClockCard";
import SmartwatchSvg from "../../../../../common/svgs/SmartwatchSvg";

/**
 * Dashboard "Bio age analysis" widget.
 * Visual reference: Figma node 15349:54135.
 *
 * Layout (top → bottom):
 *   • Title row: "Bio age analysis"
 *   • Main row (3-column):
 *       ┌──────────────┐  ┌────────────────┐  ┌──────────────┐
 *       │  ↓N years    │  │  ◯  PNOĒ age   │  │  0.87        │
 *       │  younger/    │  │     (clock)    │  │  pace of     │
 *       │  older       │  │                │  │  aging       │
 *       └──────────────┘  └────────────────┘  └──────────────┘
 *                              "Age <chrono>" caption
 *   • Bottom row: "Estimated bio age <N>"  +  Aging/Improving badge
 *
 * Clock visualization reuses `BioAgeClockFigure` from the consultation
 * (extracted from `BioAgeClockCard` so it's reusable without the
 * consultation context). The inside-circle label is overridden to
 * "PNOĒ age" per design.
 */

type BioAgeMetric = {
  initial_value?: number; // bio age measured  → center of clock (PNOĒ age)
  chronological_value?: number; // chronological age
  current_value?: number; // bio age estimated → bottom row
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

const formatDelta = (delta: number): string => {
  if (delta === 0) return "On track";
  const yrs = Math.abs(delta);
  const noun = yrs === 1 ? "year" : "years";
  return delta > 0 ? `${noun} younger` : `${noun} older`;
};

const ArrowGlyph: React.FC<{ color: string; direction: "up" | "down" }> = ({
  color,
  direction,
}) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="none"
    style={{ transform: direction === "down" ? "rotate(180deg)" : undefined }}
  >
    <path
      d="M8 13V3M8 3L4 7M8 3L12 7"
      stroke={color}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type Props = {
  isLoadingDataNut: boolean;
};

const AgeClock = ({ isLoadingDataNut }: Props): JSX.Element => {
  const theme: Theme = useTheme();

  const homeData = useSelector(
    (state: RootStatePlatform) => state.platform.dataNutServer.overview_tab
  );

  if (isLoadingDataNut) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          width: "100%",
          height: "331px",
          bgcolor: theme.colorTokens.N[30],
          borderRadius: "8px",
        }}
        animation="pulse"
      />
    );
  }

  const data: BioAgeMetric | undefined = homeData?.pnoe_metrics?.metrics?.find(
    (el: { name: string }) => el.name === "biological_age"
  );

  // Source of truth — biological=measured (initial_value), chronological,
  // estimated=current_value. Defaults to 0 so the widget still renders
  // with empty visuals if any value is missing.
  const biological = data?.initial_value ?? 0;
  const chronological = data?.chronological_value ?? 0;
  const estimated = data?.current_value ?? 0;

  // Delta = chronological − biological (positive = younger).
  const delta = chronological - biological;
  const deltaIsYounger = delta > 0;
  const deltaIsOlder = delta < 0;

  // Pace of aging = biological / chronological (rounded to 2 decimals).
  // Reads as "X years of biological aging per year of chronological aging" —
  // values below 1.0 are good (aging slower than calendar time).
  const pace = chronological > 0 ? round2(biological / chronological) : 0;
  const paceIsGood = pace > 0 && pace < 1;

  // Estimated vs. measured: if estimated > measured the projection is
  // worsening ("Aging"); if lower the projection is improving.
  const estimatedDelta = estimated - biological;
  const isAging = estimatedDelta > 0;
  const isImproving = estimatedDelta < 0;

  const deltaColor = deltaIsYounger
    ? theme.colors_ds.SUCCESS_ICON_MAIN
    : deltaIsOlder
    ? theme.colorTokens.D[60]
    : theme.colors_ds.NEUTRAL_TEXT_DEFAULT;

  const paceColor = paceIsGood
    ? theme.colors_ds.SUCCESS_ICON_MAIN
    : theme.colors_ds.NEUTRAL_TEXT_DEFAULT;

  const trendBadgeBg = isAging
    ? theme.colorTokens.D[10]
    : isImproving
    ? theme.colorTokens.S[10]
    : theme.colorTokens.N[30];
  // Arrow icon color tracks the trend tone (red/green); the label text
  // stays neutral gray to match the Figma design (#6D7075).
  const trendArrowColor = isAging
    ? theme.colorTokens.D[60]
    : isImproving
    ? theme.colors_ds.SUCCESS_ICON_MAIN
    : theme.colors_ds.NEUTRAL_TEXT_WEAK;
  const trendLabel = isAging ? "Aging" : isImproving ? "Improving" : "Stable";

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.title(theme)}>Bio age analysis</Box>

      <Box sx={styles.mainRow}>
        <Box sx={styles.sideCard(theme)}>
          <Box sx={styles.sideCardValue(deltaColor)}>
            {deltaIsYounger || deltaIsOlder ? Math.abs(delta) : "—"}
          </Box>
          <Box sx={styles.sideCardLabel(theme)}>{formatDelta(delta)}</Box>
        </Box>

        <Box sx={styles.clockColumn}>
          <BioAgeClockFigure
            biological={biological}
            chronological={chronological}
            // 128px instead of the Figma's 160px — the dashboard column
            // is narrower than the Figma reference (~410px vs ~460px),
            // and a 160px clock pushes the right-side "pace of aging"
            // card past the card border. 128px leaves ~15px of breathing
            // room that space-between distributes evenly across the gaps,
            // keeping the right and left padding visually symmetric.
            size={128}
            innerLabel="PNOĒ age"
            showSubtitle={false}
          />
          {chronological > 0 && (
            <Box sx={styles.chronoCaption(theme)}>Age {chronological}</Box>
          )}
        </Box>

        <Box sx={styles.sideCard(theme)}>
          <Box sx={styles.sideCardValue(paceColor)}>
            {chronological > 0 ? pace.toFixed(2) : "—"}
          </Box>
          <Box sx={styles.sideCardLabel(theme)}>pace of aging</Box>
        </Box>
      </Box>

      <Box sx={styles.bottomCard(theme)}>
        <Box sx={styles.bottomRow}>
          <SmartwatchSvg color={theme.colors_ds.NEUTRAL_TEXT_WEAK} />
          <Box sx={styles.bottomLabel(theme)}>Estimated bio age</Box>
          <Box sx={styles.bottomValue(theme)}>{estimated}</Box>
        </Box>
        {(isAging || isImproving) && (
          <Box sx={styles.trendBadge(trendBadgeBg)}>
            <ArrowGlyph
              color={trendArrowColor}
              direction={isAging ? "down" : "up"}
            />
            <Box sx={styles.trendLabel(theme)}>{trendLabel}</Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AgeClock;

const styles = {
  container: (theme: Theme) => ({
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  }),
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  mainRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "0 8px",
  },
  sideCard: (theme: Theme) => ({
    flexShrink: 0,
    minWidth: "92px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `0.5px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "8px",
    boxShadow:
      "0px 10px 7.5px rgba(59, 63, 66, 0.1), 0px 4px 3px rgba(59, 63, 66, 0.05)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
  }),
  sideCardValue: (color: string) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "20px",
    lineHeight: 1.4,
    color,
    textAlign: "center" as const,
  }),
  sideCardLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as const,
  }),
  clockColumn: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "4px",
  },
  chronoCaption: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
  }),
  bottomCard: (theme: Theme) => ({
    width: "100%",
    minHeight: "41px",
    padding: "8px",
    borderRadius: "8px",
    border: `0.3px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxShadow:
      "0px 10px 7.5px rgba(59, 63, 66, 0.1), 0px 4px 3px rgba(59, 63, 66, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  }),
  bottomRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  bottomLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  bottomValue: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  trendBadge: (bg: string) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 8px",
    borderRadius: "8px",
    backgroundColor: bg,
  }),
  trendLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    // Always neutral gray regardless of trend direction — matches Figma
    // (#6D7075). The arrow icon alone carries the red/green tone.
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
